import type { FC, ReactNode } from 'react'

import { Fragment, useEffect, useRef, useState } from 'react'

import {
    motion,
    useAnimationFrame,
    useInView,
    useMotionValue,
    useScroll,
    useSpring,
    useTransform,
    useVelocity
} from 'motion/react'

import { cn } from '@/lib/utils'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'

interface VelocityMarqueeProps {
    items: string[]
    baseVelocity?: number
    className?: string
    itemClassName?: string
    separator?: ReactNode
}

const COPIES = 4

const wrap = (min: number, max: number, value: number): number => {
    const range = max - min

    return ((((value - min) % range) + range) % range) + min
}

// A looping text band that drifts on its own, speeds up with the scroll and
// flips direction when the visitor scrolls back up.
export const VelocityMarquee: FC<VelocityMarqueeProps> = ({
    items,
    baseVelocity = 60,
    className,
    itemClassName,
    separator
}): ReactNode => {
    const containerRef = useRef<HTMLDivElement>(null)
    const copyRef = useRef<HTMLDivElement>(null)
    const direction = useRef(1)
    const [copyWidth, setCopyWidth] = useState(0)
    const reduced = usePrefersReducedMotion()
    const inView = useInView(containerRef, { margin: '200px 0px' })

    const offset = useMotionValue(0)
    const { scrollY } = useScroll()
    const scrollVelocity = useVelocity(scrollY)
    const smoothVelocity = useSpring(scrollVelocity, { damping: 50, stiffness: 400 })
    const velocityFactor = useTransform(smoothVelocity, [0, 1000], [0, 4], { clamp: false })
    const skewX = useTransform(smoothVelocity, [-2500, 0, 2500], reduced ? [0, 0, 0] : [7, 0, -7])
    const x = useTransform(offset, value => copyWidth > 0 ? -wrap(0, copyWidth, value) : 0)

    useEffect(() => {
        const element = copyRef.current

        if (!element) return

        const measure = () => setCopyWidth(element.getBoundingClientRect().width)
        const observer = new ResizeObserver(measure)

        measure()
        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    useAnimationFrame((_, delta) => {
        if (reduced || !inView || copyWidth === 0) return

        const factor = velocityFactor.get()

        if (factor < 0) direction.current = -1
        else if (factor > 0) direction.current = 1

        let moveBy = direction.current * baseVelocity * (delta / 1000)

        moveBy += direction.current * moveBy * factor
        offset.set(offset.get() + moveBy)
    })

    return (
        <div ref={containerRef} className={cn('flex overflow-hidden whitespace-nowrap', className)}>
            <motion.div className='flex shrink-0 will-change-transform' style={{ x, skewX }}>
                {Array.from({ length: COPIES }, (_, copy) => (
                    <div key={copy} ref={copy === 0 ? copyRef : undefined} className='flex shrink-0 items-center'>
                        {items.map((item, index) => (
                            <Fragment key={`${copy}-${item}-${index}`}>
                                <span className={cn('px-5 md:px-8', itemClassName)}>
                                    {item}
                                </span>

                                {separator}
                            </Fragment>
                        ))}
                    </div>
                ))}
            </motion.div>
        </div>
    )
}
